import React, { useState, useEffect, useCallback } from 'react';
import { Key, Bot, Plus, Trash2, Eye, EyeOff, RefreshCw, Power, AlertCircle, Check, FileText, Send, Loader, X, TrendingUp, TrendingDown, Lock, Users, UserPlus, Edit2, Shield } from 'lucide-react';
import '../styles/SettingsPanel.css';

function SettingsPanel({ userRole = 'admin', currentUser = '' }) {
  const isAdmin = userRole === 'admin';
  
  const [apiKeys, setApiKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [botStatus, setBotStatus] = useState(null);
  const [message, setMessage] = useState(null);
  const [showSecrets, setShowSecrets] = useState({});
  
  // Password change state
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [passwordData, setPasswordData] = useState({ current: '', new: '', confirm: '' });
  const [passwordLoading, setPasswordLoading] = useState(false);
  
  // User management state (admin only)
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [showAddUserForm, setShowAddUserForm] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [newUser, setNewUser] = useState({ username: '', password: '', role: 'trader' });
  const [userActionLoading, setUserActionLoading] = useState(null);

  // Daily Report state
  const [reportSettings, setReportSettings] = useState(null);
  const [reportLoading, setReportLoading] = useState(false);
  const [reportPreview, setReportPreview] = useState(null);
  const [entryPrice, setEntryPrice] = useState('');
  const [direction, setDirection] = useState('down');
  const [reportStatus, setReportStatus] = useState({ running: false });

  const [newKey, setNewKey] = useState({
    exchange: isAdmin ? 'coindcx' : 'hyperliquid',  // Traders default to Hyperliquid
    name: '',
    api_key: '',
    api_secret: '',
    passphrase: ''
  });

  // Close preview with ESC key
  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Escape' && reportPreview) {
      setReportPreview(null);
    }
  }, [reportPreview]);

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  // Fetch data on mount
  useEffect(() => {
    fetchApiKeys(); // All users can fetch their own API keys
    if (isAdmin) {
      fetchUsers();
      fetchBotStatus();
      fetchReportSettings();
      fetchReportStatus();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin]);

  const fetchApiKeys = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/settings/api-keys`,
        { credentials: 'include' }
      );
      if (response.ok) {
        const data = await response.json();
        setApiKeys(data.keys || []);
      }
    } catch (err) {
      console.error('Fetch API keys error:', err);
    } finally {
      setLoading(false);
    }
  };

  // User management functions (admin only)
  const fetchUsers = async () => {
    if (!isAdmin) return;
    setUsersLoading(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/admin/users`,
        { credentials: 'include' }
      );
      if (response.ok) {
        const data = await response.json();
        setUsers(data.users || []);
      }
    } catch (err) {
      console.error('Fetch users error:', err);
    } finally {
      setUsersLoading(false);
    }
  };

  const handleAddUser = async (e) => {
    e.preventDefault();
    if (!newUser.username || !newUser.password) {
      setMessage({ type: 'error', text: 'Username and password are required' });
      setTimeout(() => setMessage(null), 3000);
      return;
    }
    
    setUserActionLoading('add');
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/admin/users`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify(newUser)
        }
      );
      
      if (response.ok) {
        setMessage({ type: 'success', text: `User '${newUser.username}' created` });
        setNewUser({ username: '', password: '', role: 'trader' });
        setShowAddUserForm(false);
        fetchUsers();
      } else {
        const data = await response.json();
        setMessage({ type: 'error', text: data.detail || 'Failed to create user' });
      }
    } catch (err) {
      setMessage({ type: 'error', text: 'Connection error' });
    } finally {
      setUserActionLoading(null);
      setTimeout(() => setMessage(null), 3000);
    }
  };

  const handleUpdateUser = async (userId, updates) => {
    setUserActionLoading(userId);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/admin/users/${userId}`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify(updates)
        }
      );
      
      if (response.ok) {
        setMessage({ type: 'success', text: 'User updated' });
        setEditingUser(null);
        fetchUsers();
      } else {
        const data = await response.json();
        setMessage({ type: 'error', text: data.detail || 'Failed to update user' });
      }
    } catch (err) {
      setMessage({ type: 'error', text: 'Connection error' });
    } finally {
      setUserActionLoading(null);
      setTimeout(() => setMessage(null), 3000);
    }
  };

  const handleDeleteUser = async (userId, username) => {
    if (!window.confirm(`Delete user '${username}'? This cannot be undone.`)) return;
    
    setUserActionLoading(userId);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/admin/users/${userId}`,
        {
          method: 'DELETE',
          credentials: 'include'
        }
      );
      
      if (response.ok) {
        setMessage({ type: 'success', text: `User '${username}' deleted` });
        fetchUsers();
      } else {
        const data = await response.json();
        setMessage({ type: 'error', text: data.detail || 'Failed to delete user' });
      }
    } catch (err) {
      setMessage({ type: 'error', text: 'Connection error' });
    } finally {
      setUserActionLoading(null);
      setTimeout(() => setMessage(null), 3000);
    }
  };

  const handleToggleUserActive = async (user) => {
    await handleUpdateUser(user.id, { is_active: !user.is_active });
  };

  const fetchBotStatus = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/telegram/status`,
        { credentials: 'include' }
      );
      if (response.ok) {
        const data = await response.json();
        setBotStatus(data);
      }
    } catch (err) {
      console.error('Fetch bot status error:', err);
    }
  };

  const fetchReportSettings = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/report/settings`,
        { credentials: 'include' }
      );
      if (response.ok) {
        const data = await response.json();
        setReportSettings(data.settings);
        setEntryPrice(data.settings?.btc_entry_price?.toString() || '97000');
        setDirection(data.settings?.direction || 'down');
      }
    } catch (err) {
      console.error('Fetch report settings error:', err);
    }
  };

  const fetchReportStatus = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/report/status`,
        { credentials: 'include' }
      );
      if (response.ok) {
        const data = await response.json();
        setReportStatus(data);
      }
    } catch (err) {
      console.error('Fetch report status error:', err);
    }
  };

  const handleUpdateSettings = async (updates) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/report/settings`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify(updates)
        }
      );
      if (response.ok) {
        fetchReportSettings();
        setMessage({ type: 'success', text: 'Settings updated' });
        setTimeout(() => setMessage(null), 2000);
      } else {
        setMessage({ type: 'error', text: 'Failed to update settings' });
        setTimeout(() => setMessage(null), 3000);
      }
    } catch (err) {
      setMessage({ type: 'error', text: 'Failed to update settings' });
      setTimeout(() => setMessage(null), 3000);
    }
  };

  const handleUpdateEntryPrice = () => {
    handleUpdateSettings({ btc_entry_price: parseFloat(entryPrice) });
  };

  const handleDirectionChange = (newDirection) => {
    setDirection(newDirection);
    handleUpdateSettings({ direction: newDirection });
  };

  const handleReportAction = async (action) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/report/${action}`,
        {
          method: 'POST',
          credentials: 'include'
        }
      );
      if (response.ok) {
        const data = await response.json();
        setMessage({ type: 'success', text: data.message || `Report scheduler ${action}` });
        fetchReportStatus();
      } else {
        const data = await response.json();
        setMessage({ type: 'error', text: data.detail || `Failed to ${action}` });
      }
    } catch (err) {
      setMessage({ type: 'error', text: 'Connection error' });
    }
    setTimeout(() => setMessage(null), 3000);
  };

  const handleGenerateReport = async (sendToTelegram = false) => {
    setReportLoading(true);
    setReportPreview(null);
    
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/report/generate`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ 
            send_telegram: sendToTelegram,
            preview_only: !sendToTelegram,
            direction: direction
          })
        }
      );
      
      if (response.ok) {
        const data = await response.json();
        setReportPreview(data.image_base64);
        // Only show message if sent to Telegram
        if (sendToTelegram) {
          setMessage({ type: 'success', text: 'Report sent to Telegram!' });
          setTimeout(() => setMessage(null), 3000);
        }
      } else {
        const data = await response.json();
        setMessage({ type: 'error', text: data.detail || 'Failed to generate report' });
        setTimeout(() => setMessage(null), 3000);
      }
    } catch (err) {
      setMessage({ type: 'error', text: 'Connection error' });
      setTimeout(() => setMessage(null), 3000);
    } finally {
      setReportLoading(false);
    }
  };

  const handleClosePreview = () => {
    setReportPreview(null);
  };

  const handleAddKey = async (e) => {
    e.preventDefault();
    setMessage(null);

    const isHyperliquid = newKey.exchange === 'hyperliquid';
    const needsPassphrase = ['blofin', 'okx', 'kucoin'].includes(newKey.exchange);
    if (!newKey.name || !newKey.api_key || (!isHyperliquid && !newKey.api_secret)) {
      setMessage({ type: 'error', text: 'Please fill in all required fields' });
      return;
    }
    if (needsPassphrase && !newKey.passphrase) {
      setMessage({ type: 'error', text: 'Please enter the API passphrase' });
      return;
    }

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/settings/api-keys`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify(newKey)
        }
      );

      if (response.ok) {
        setMessage({ type: 'success', text: 'API key added successfully' });
        setNewKey({ exchange: 'coindcx', name: '', api_key: '', api_secret: '', passphrase: '' });
        setShowAddForm(false);
        fetchApiKeys();
      } else {
        const data = await response.json();
        setMessage({ type: 'error', text: data.detail || 'Failed to add API key' });
      }
    } catch (err) {
      setMessage({ type: 'error', text: 'Connection error' });
    }
    setTimeout(() => setMessage(null), 3000);
  };

  const handleDeleteKey = async (keyId) => {
    if (!window.confirm('Are you sure you want to delete this API key?')) return;

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/settings/api-keys/${keyId}`,
        {
          method: 'DELETE',
          credentials: 'include'
        }
      );

      if (response.ok) {
        setMessage({ type: 'success', text: 'API key deleted' });
        fetchApiKeys();
      }
    } catch (err) {
      setMessage({ type: 'error', text: 'Failed to delete API key' });
    }
    setTimeout(() => setMessage(null), 3000);
  };

  const handleBotAction = async (action) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/telegram/${action}`,
        {
          method: 'POST',
          credentials: 'include'
        }
      );

      if (response.ok) {
        const data = await response.json();
        setMessage({ type: 'success', text: data.message || `Bot ${action} successful` });
        fetchBotStatus();
      } else {
        const data = await response.json();
        setMessage({ type: 'error', text: data.detail || `Failed to ${action} bot` });
      }
    } catch (err) {
      setMessage({ type: 'error', text: 'Connection error' });
    }
    setTimeout(() => setMessage(null), 3000);
  };

  const toggleShowSecret = (keyId) => {
    setShowSecrets(prev => ({ ...prev, [keyId]: !prev[keyId] }));
  };

  const maskSecret = (secret) => {
    if (!secret) return '';
    return secret.substring(0, 4) + '........' + secret.substring(secret.length - 4);
  };

  // Available exchanges - Traders only see Hyperliquid (other exchanges use env vars)
  const allExchanges = [
    { id: 'coindcx', name: 'CoinDCX', adminOnly: true },
    { id: 'hyperliquid', name: 'Hyperliquid', adminOnly: false },
    { id: 'binance', name: 'Binance', adminOnly: true },
    { id: 'bybit', name: 'Bybit', adminOnly: true },
    { id: 'okx', name: 'OKX', adminOnly: true },
    { id: 'kucoin', name: 'KuCoin', adminOnly: true },
    { id: 'blofin', name: 'BloFin', adminOnly: true },
    { id: 'bitget', name: 'Bitget', adminOnly: true },
    { id: 'mexc', name: 'MEXC', adminOnly: true },
  ];
  
  // Filter exchanges based on role
  const exchanges = isAdmin 
    ? allExchanges 
    : allExchanges.filter(ex => !ex.adminOnly);

  // Password change handler
  const handlePasswordChange = async (e) => {
    e.preventDefault();
    
    if (passwordData.new !== passwordData.confirm) {
      setMessage({ type: 'error', text: 'New passwords do not match' });
      setTimeout(() => setMessage(null), 3000);
      return;
    }
    
    if (passwordData.new.length < 8) {
      setMessage({ type: 'error', text: 'Password must be at least 8 characters' });
      setTimeout(() => setMessage(null), 3000);
      return;
    }
    
    setPasswordLoading(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/auth/change-password`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            current_password: passwordData.current,
            new_password: passwordData.new
          })
        }
      );
      
      if (response.ok) {
        setMessage({ type: 'success', text: 'Password changed successfully' });
        setPasswordData({ current: '', new: '', confirm: '' });
        setShowPasswordForm(false);
      } else {
        const data = await response.json();
        setMessage({ type: 'error', text: data.detail || 'Failed to change password' });
      }
    } catch (err) {
      setMessage({ type: 'error', text: 'Connection error' });
    }
    setPasswordLoading(false);
    setTimeout(() => setMessage(null), 3000);
  };

  return (
    <div className="settings-panel">
      {message && (
        <div className={`settings-message ${message.type}`}>
          {message.type === 'success' ? <Check size={16} /> : <AlertCircle size={16} />}
          <span>{message.text}</span>
        </div>
      )}

      {/* Telegram Bot Section - Admin Only */}
      {isAdmin && (
      <div className="settings-section">
        <div className="section-header">
          <div className="section-title">
            <Bot size={20} />
            <h3>Telegram Bot</h3>
          </div>
        </div>

        <div className="bot-status-card">
          <div className="bot-info">
            <div className="bot-indicator">
              {/* Tri-state (2026-08-30): 'stopping' = stop accepted, thread
                  draining its last sweep — clicking Stop again does nothing */}
              <span className={`status-dot ${botStatus?.running ? 'online' : 'offline'}`}></span>
              <span className="status-text">
                {botStatus?.state === 'stopping' ? 'Stopping…'
                  : botStatus?.running ? 'Running' : 'Stopped'}
              </span>
            </div>
            {botStatus?.last_check && (
              <span className="last-check">Last check: {new Date(botStatus.last_check).toLocaleTimeString()}</span>
            )}
          </div>

          <div className="bot-actions">
            <button 
              className="bot-btn start"
              onClick={() => handleBotAction('start')}
              disabled={botStatus?.running}
            >
              <Power size={14} />
              Start
            </button>
            <button 
              className="bot-btn stop"
              onClick={() => handleBotAction('stop')}
              disabled={!botStatus?.running}
            >
              <Power size={14} />
              Stop
            </button>
            <button 
              className="bot-btn restart"
              onClick={() => handleBotAction('restart')}
            >
              <RefreshCw size={14} />
              Restart
            </button>
          </div>
        </div>

        <div className="bot-commands">
          <h4>Available Commands</h4>
          <div className="commands-list">
            <div className="command-item">
              <code>/positions</code>
              <span>Get current open positions</span>
            </div>
            <div className="command-item">
              <code>/pnl</code>
              <span>Get total P&L summary</span>
            </div>
            <div className="command-item">
              <code>/status</code>
              <span>Check bot status</span>
            </div>
          </div>
        </div>

        {/* Daily Report Sub-Section */}
        <div className="daily-report-subsection">
          <div className="subsection-header">
            <div className="subsection-title">
              <FileText size={18} />
              <h4>Daily Market Report</h4>
            </div>
            <div className="subsection-status">
              <span className={`status-dot ${reportStatus?.running ? 'online' : 'offline'}`}></span>
              <span className="status-text-small">{reportStatus?.running ? 'Active' : 'Stopped'}</span>
            </div>
          </div>

          <div className="report-controls">
            <div className="report-schedule-info">
              <span className="schedule-label">Schedule:</span>
              <span className="schedule-value">
                {reportSettings?.schedule?.time || '06:00 AM IST'} ({reportSettings?.schedule?.days || 'Mon-Fri'})
              </span>
            </div>
            
            <div className="report-actions-row">
              <button 
                className="control-btn start"
                onClick={() => handleReportAction('start')}
                disabled={reportStatus?.running}
              >
                <Power size={14} />
                Start
              </button>
              <button 
                className="control-btn stop"
                onClick={() => handleReportAction('stop')}
                disabled={!reportStatus?.running}
              >
                <Power size={14} />
                Stop
              </button>
              <button 
                className="control-btn restart"
                onClick={() => handleReportAction('restart')}
              >
                <RefreshCw size={14} />
                Restart
              </button>
            </div>
          </div>

          <div className="report-entry-price">
            <label>BTC Entry Price (for Gann Levels)</label>
            <div className="entry-price-row">
              <div className="entry-price-input">
                <span className="currency">$</span>
                <input
                  type="number"
                  value={entryPrice}
                  onChange={(e) => setEntryPrice(e.target.value)}
                  placeholder="97000"
                />
                <button 
                  className="btn-update"
                  onClick={handleUpdateEntryPrice}
                >
                  Update
                </button>
              </div>
              
              <div className="direction-selector">
                <button 
                  className={`direction-btn down ${direction === 'down' ? 'active' : ''}`}
                  onClick={() => handleDirectionChange('down')}
                  title="Bearish - Focus on support levels"
                >
                  <TrendingDown size={14} />
                  Down
                </button>
                <button 
                  className={`direction-btn up ${direction === 'up' ? 'active' : ''}`}
                  onClick={() => handleDirectionChange('up')}
                  title="Bullish - Focus on resistance levels"
                >
                  <TrendingUp size={14} />
                  Up
                </button>
              </div>
            </div>
          </div>

          <div className="report-generate-actions">
            <button 
              className="report-btn preview"
              onClick={() => handleGenerateReport(false)}
              disabled={reportLoading}
            >
              {reportLoading ? <Loader size={14} className="spinning" /> : <Eye size={14} />}
              Preview
            </button>
            <button 
              className="report-btn send"
              onClick={() => handleGenerateReport(true)}
              disabled={reportLoading || !reportSettings?.telegram_configured}
              title={!reportSettings?.telegram_configured ? 'Telegram not configured' : 'Generate and send'}
            >
              {reportLoading ? <Loader size={14} className="spinning" /> : <Send size={14} />}
              Send to Telegram
            </button>
          </div>

          {!reportSettings?.telegram_configured && (
            <div className="telegram-warning">
              <AlertCircle size={14} />
              <span>Telegram not configured</span>
            </div>
          )}
        </div>
      </div>
      )}

      {/* Report Preview Modal - Admin Only */}
      {isAdmin && reportPreview && (
        <div className="report-preview-overlay" onClick={handleClosePreview}>
          <div className="report-preview-modal" onClick={(e) => e.stopPropagation()}>
            <div className="preview-header">
              <h4>Report Preview</h4>
              <button className="preview-close-btn" onClick={handleClosePreview} title="Close (Esc)">
                <X size={16} />
              </button>
            </div>
            <div className="preview-content">
              <img 
                src={`data:image/png;base64,${reportPreview}`} 
                alt="Daily Report Preview" 
              />
            </div>
            <div className="preview-footer">
              <button className="btn-secondary" onClick={handleClosePreview}>
                Close
              </button>
              <button 
                className="btn-primary"
                onClick={() => handleGenerateReport(true)}
                disabled={reportLoading || !reportSettings?.telegram_configured}
              >
                <Send size={14} />
                Send to Telegram
              </button>
            </div>
          </div>
        </div>
      )}

      {/* API Keys Section - All Users */}
      <div className="settings-section">
        <div className="section-header">
          <div className="section-title">
            <Key size={20} />
            <h3>Exchange API Keys</h3>
          </div>
          <button 
            className="add-key-btn"
            onClick={() => setShowAddForm(!showAddForm)}
          >
            <Plus size={16} />
            Add Exchange
          </button>
        </div>

        {showAddForm && (
          <form className="add-key-form" onSubmit={handleAddKey}>
            <div className="form-row">
              <div className="form-group">
                <label>Exchange</label>
                <select 
                  value={newKey.exchange}
                  onChange={(e) => setNewKey({...newKey, exchange: e.target.value})}
                >
                  {exchanges.map(ex => (
                    <option key={ex.id} value={ex.id}>{ex.name}</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>Account Name</label>
                <input
                  type="text"
                  placeholder="e.g., Main Account"
                  value={newKey.name}
                  onChange={(e) => setNewKey({...newKey, name: e.target.value})}
                />
              </div>
            </div>

            <div className="form-group">
              <label>{newKey.exchange === 'hyperliquid' ? 'Wallet Address' : 'API Key'}</label>
              <input
                type="text"
                placeholder={newKey.exchange === 'hyperliquid' ? 'Enter wallet address (0x...)' : 'Enter API key'}
                value={newKey.api_key}
                onChange={(e) => setNewKey({...newKey, api_key: e.target.value})}
              />
            </div>

            <div className="form-group">
              <label>{newKey.exchange === 'hyperliquid' ? 'Private Key (Optional)' : 'API Secret'}</label>
              <input
                type="password"
                placeholder={newKey.exchange === 'hyperliquid' ? 'Leave empty for read-only' : 'Enter API secret'}
                value={newKey.api_secret}
                onChange={(e) => setNewKey({...newKey, api_secret: e.target.value})}
              />
            </div>

            {(['blofin', 'okx', 'kucoin'].includes(newKey.exchange)) && (
              <div className="form-group">
                <label>Passphrase</label>
                <input
                  type="password"
                  placeholder="Enter passphrase"
                  value={newKey.passphrase}
                  onChange={(e) => setNewKey({...newKey, passphrase: e.target.value})}
                />
              </div>
            )}

            <div className="form-actions">
              <button type="button" className="btn-cancel" onClick={() => setShowAddForm(false)}>
                Cancel
              </button>
              <button type="submit" className="btn-submit">
                Add API Key
              </button>
            </div>
          </form>
        )}

        {loading ? (
          <div className="loading-keys">Loading API keys...</div>
        ) : apiKeys.length === 0 ? (
          <div className="no-keys">
            <Key size={32} />
            <p>No API keys configured</p>
            <span>Add your exchange API keys to connect additional accounts</span>
          </div>
        ) : (
          <div className="keys-list">
            {apiKeys.map(key => (
              <div key={key.id} className="key-card">
                <div className="key-header">
                  <div className="key-info">
                    <span className="key-exchange">{key.exchange.toUpperCase()}</span>
                    <span className="key-name">{key.name}</span>
                  </div>
                  <div className="key-actions">
                    <button 
                      className="icon-btn"
                      onClick={() => toggleShowSecret(key.id)}
                      title={showSecrets[key.id] ? 'Hide' : 'Show'}
                    >
                      {showSecrets[key.id] ? <EyeOff size={14} /> : <Eye size={14} />}
                    </button>
                    <button 
                      className="icon-btn delete"
                      onClick={() => handleDeleteKey(key.id)}
                      title="Delete"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
                <div className="key-details">
                  <div className="key-field">
                    <span className="field-label">API Key</span>
                    <code>{showSecrets[key.id] ? key.api_key : maskSecret(key.api_key)}</code>
                  </div>
                </div>
                <div className="key-status">
                  <span className={`status-badge ${key.active ? 'active' : 'inactive'}`}>
                    {key.active ? 'Active' : 'Inactive'}
                  </span>
                  <span className="key-added">Added {new Date(key.created_at).toLocaleDateString()}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* User Management Section - Admin Only */}
      {isAdmin && (
      <div className="settings-section">
        <div className="section-header">
          <div className="section-title">
            <Users size={20} />
            <h3>User Management</h3>
          </div>
          <button 
            className="add-btn"
            onClick={() => setShowAddUserForm(!showAddUserForm)}
          >
            <UserPlus size={16} />
            Add User
          </button>
        </div>

        {showAddUserForm && (
          <form className="add-user-form" onSubmit={handleAddUser}>
            <div className="form-row">
              <div className="form-group">
                <label>Username</label>
                <input
                  type="text"
                  placeholder="Enter username"
                  value={newUser.username}
                  onChange={(e) => setNewUser({...newUser, username: e.target.value})}
                  required
                  minLength={3}
                  maxLength={50}
                />
              </div>
              <div className="form-group">
                <label>Role</label>
                <div className="role-display">
                  <Shield size={14} />
                  <span>Trader</span>
                </div>
              </div>
            </div>
            <div className="form-group">
              <label>Password</label>
              <input
                type="password"
                placeholder="Enter password (min 8 characters)"
                value={newUser.password}
                onChange={(e) => setNewUser({...newUser, password: e.target.value})}
                required
                minLength={8}
              />
            </div>
            <div className="form-actions">
              <button type="button" className="btn-cancel" onClick={() => {
                setShowAddUserForm(false);
                setNewUser({ username: '', password: '', role: 'trader' });
              }}>
                Cancel
              </button>
              <button type="submit" className="btn-submit" disabled={userActionLoading === 'add'}>
                {userActionLoading === 'add' ? <Loader size={14} className="spinning" /> : 'Create User'}
              </button>
            </div>
          </form>
        )}

        {usersLoading ? (
          <div className="loading-keys">Loading users...</div>
        ) : users.length === 0 ? (
          <div className="no-keys">
            <Users size={32} />
            <p>No users configured</p>
            <span>Add users to enable multi-user access</span>
          </div>
        ) : (
          <div className="users-list">
            {users.map(user => (
              <div key={user.id} className={`user-card ${!user.is_active ? 'inactive' : ''}`}>
                <div className="user-header">
                  <div className="user-info">
                    <span className="user-name">{user.username}</span>
                    <span className={`role-badge ${user.role}`}>
                      <Shield size={12} />
                      {user.role.toUpperCase()}
                    </span>
                  </div>
                  <div className="user-actions">
                    {/* Hide deactivate for: own account OR admin users when logged in as legacy admin */}
                    {user.username !== currentUser && !(currentUser === 'dashboard' && user.role === 'admin') && (
                    <button 
                      className={`icon-btn ${user.is_active ? 'active' : 'inactive'}`}
                      onClick={() => handleToggleUserActive(user)}
                      title={user.is_active ? 'Deactivate' : 'Activate'}
                      disabled={userActionLoading === user.id}
                    >
                      <Power size={14} />
                    </button>
                    )}
                    <button 
                      className="icon-btn edit"
                      onClick={() => setEditingUser(editingUser === user.id ? null : user.id)}
                      title="Edit"
                    >
                      <Edit2 size={14} />
                    </button>
                    {/* Hide delete for: own account OR admin users when logged in as legacy admin */}
                    {user.username !== currentUser && !(currentUser === 'dashboard' && user.role === 'admin') && (
                    <button 
                      className="icon-btn delete"
                      onClick={() => handleDeleteUser(user.id, user.username)}
                      title="Delete"
                      disabled={userActionLoading === user.id}
                    >
                      <Trash2 size={14} />
                    </button>
                    )}
                  </div>
                </div>
                
                {editingUser === user.id && (
                  <div className="user-edit-form">
                    <div className="form-row">
                      <div className="form-group">
                        <label>New Password (leave empty to keep current)</label>
                        <input
                          type="password"
                          placeholder="New password"
                          id={`password-${user.id}`}
                        />
                      </div>
                      <div className="form-group">
                        <label>Role</label>
                        <select
                          id={`role-${user.id}`}
                          defaultValue={user.role}
                        >
                          <option value="trader">Trader</option>
                          <option value="admin">Admin</option>
                        </select>
                      </div>
                    </div>
                    <div className="form-actions">
                      <button 
                        type="button" 
                        className="btn-cancel"
                        onClick={() => setEditingUser(null)}
                      >
                        Cancel
                      </button>
                      <button 
                        type="button" 
                        className="btn-submit"
                        disabled={userActionLoading === user.id}
                        onClick={() => {
                          const newPassword = document.getElementById(`password-${user.id}`).value;
                          const newRole = document.getElementById(`role-${user.id}`).value;
                          const updates = {};
                          if (newPassword) updates.password = newPassword;
                          if (newRole !== user.role) updates.role = newRole;
                          if (Object.keys(updates).length > 0) {
                            handleUpdateUser(user.id, updates);
                          } else {
                            setEditingUser(null);
                          }
                        }}
                      >
                        {userActionLoading === user.id ? <Loader size={14} className="spinning" /> : 'Save Changes'}
                      </button>
                    </div>
                  </div>
                )}

                <div className="user-meta">
                  <span className={`status-badge ${user.is_active ? 'active' : 'inactive'}`}>
                    {user.is_active ? 'Active' : 'Inactive'}
                  </span>
                  {user.last_login && (
                    <span className="last-login">Last login: {new Date(user.last_login).toLocaleString()}</span>
                  )}
                  <span className="user-created">Created: {new Date(user.created_at).toLocaleDateString()}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      )}

      {/* Security Section - Password Change */}
      <div className="settings-section">
        <div className="section-header">
          <div className="section-title">
            <Lock size={20} />
            <h3>Security</h3>
          </div>
          <button className="add-btn" onClick={() => setShowPasswordForm(!showPasswordForm)}>
            {showPasswordForm ? 'Cancel' : 'Change Password'}
          </button>
        </div>

        {showPasswordForm && (
          <form className="password-form" onSubmit={handlePasswordChange}>
            <div className="form-group">
              <label>Current Password</label>
              <input
                type="password"
                placeholder="Enter current password"
                value={passwordData.current}
                onChange={(e) => setPasswordData({...passwordData, current: e.target.value})}
                required
              />
            </div>
            <div className="form-group">
              <label>New Password</label>
              <input
                type="password"
                placeholder="Enter new password (min 8 characters)"
                value={passwordData.new}
                onChange={(e) => setPasswordData({...passwordData, new: e.target.value})}
                required
                minLength={8}
              />
            </div>
            <div className="form-group">
              <label>Confirm New Password</label>
              <input
                type="password"
                placeholder="Confirm new password"
                value={passwordData.confirm}
                onChange={(e) => setPasswordData({...passwordData, confirm: e.target.value})}
                required
              />
            </div>
            <div className="form-actions">
              <button type="button" className="btn-cancel" onClick={() => {
                setShowPasswordForm(false);
                setPasswordData({ current: '', new: '', confirm: '' });
              }}>
                Cancel
              </button>
              <button type="submit" className="btn-submit" disabled={passwordLoading}>
                {passwordLoading ? <Loader size={14} className="spinning" /> : 'Change Password'}
              </button>
            </div>
          </form>
        )}

        <div className="security-info">
          <p>Password must be at least 8 characters. Choose a strong password that you don't use elsewhere.</p>
        </div>
      </div>
    </div>
  );
}

export default SettingsPanel;
